import React, { useEffect, useMemo, useState } from 'react';
import ApiService from '../services/ApiService';
import ChartCard from '../components/ChartCard';
import LoadingIndicator from '../components/LoadingIndicator';

const INDEX_SYMBOLS = [
  { symbol: 'VTI', name: 'Vanguard Total Stock Market ETF' },
  { symbol: 'DIA', name: 'SPDR Dow Jones Industrial Average ETF' },
  { symbol: 'SPY', name: 'S&P 500 ETF' },
  { symbol: 'QQQ', name: 'Nasdaq 100 ETF' },
];

const MissingIndexPlaceholder = ({ symbol, name }) => (
  <div className="ste-card border-dashed border-2 border-gray-200 bg-gray-50">
    <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="text-xl font-bold text-navy-900 mb-1">{symbol}</h3>
          <p className="text-xs text-gray-600">{name}</p>
        </div>
        <div className="px-3 py-1.5 rounded-lg font-bold text-xs bg-gray-100 text-gray-500">
          Missing
        </div>
      </div>
    </div>

    <div className="relative aspect-video bg-white flex items-center justify-center">
      <div className="text-center px-6 text-gray-400">
        <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6a2 2 0 012-2h2m-4 8h8m-8 0a2 2 0 01-2-2V7a2 2 0 012-2h5l5 5v5a2 2 0 01-2 2m-8 0h8" />
        </svg>
        <p className="mt-2 text-sm font-medium text-gray-500">Chart not available</p>
        <p className="text-xs mt-1">{symbol} was not found in the current database.</p>
      </div>
    </div>

    <div className="p-4">
      <p className="text-sm text-gray-500">
        This index ETF will appear here automatically after it is included in a future data import.
      </p>
    </div>
  </div>
);

const IndexesPage = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataFreshness, setDataFreshness] = useState(null);

  useEffect(() => {
    ApiService.getMetadata()
      .then(metadata => {
        if (metadata.last_updated) {
          setDataFreshness(new Date(metadata.last_updated).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
          }));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchIndexes = async () => {
      setLoading(true);
      setError(null);

      try {
        const symbols = INDEX_SYMBOLS.map(index => index.symbol).join(',');
        const result = await ApiService.getRecords({ symbol: symbols }, 1, INDEX_SYMBOLS.length, 'symbol_asc');
        setStocks(result.data || []);
      } catch (err) {
        console.error('IndexesPage fetch error:', err);
        setError('Failed to load index charts. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchIndexes();
  }, []);

  const stocksBySymbol = useMemo(() => {
    return stocks.reduce((acc, stock) => {
      acc[String(stock.symbol || '').toUpperCase()] = stock;
      return acc;
    }, {});
  }, [stocks]);

  return (
    <div>
      <div className="bg-navy-900 rounded-xl px-6 py-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">📈</span>
            <h1 className="text-xl font-bold text-white">Indexes</h1>
          </div>
          <p className="text-sm text-navy-300 max-w-2xl">
            A fixed watchlist of major market index ETFs: VTI, DIA, SPY, and QQQ.
          </p>
        </div>
        {dataFreshness && (
          <div className="text-xs text-navy-400 whitespace-nowrap">
            Data updated: <span className="text-navy-200 font-medium">{dataFreshness}</span>
          </div>
        )}
        {/* Build info */}
        {(process.env.REACT_APP_BUILD_NUMBER || process.env.REACT_APP_BUILD_DATE) && (
          <div className="text-[10px] text-navy-500 whitespace-nowrap flex items-center gap-1.5 mt-1">
            {process.env.REACT_APP_BUILD_NUMBER && (
              <span className="font-mono bg-gray-100 px-1 py-0.5 rounded">
                Build #{process.env.REACT_APP_BUILD_NUMBER}
              </span>
            )}
            {process.env.REACT_APP_BUILD_DATE && (
              <span className="font-mono bg-gray-100 px-1 py-0.5 rounded">
                {new Date(process.env.REACT_APP_BUILD_DATE).toLocaleDateString('en-US', { 
                  year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                })}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {INDEX_SYMBOLS.map(({ symbol, name }) => (
          <span key={symbol} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {symbol} — {name}
          </span>
        ))}
      </div>

      {loading ? (
        <LoadingIndicator />
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 text-sm">{error}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {INDEX_SYMBOLS.map(({ symbol, name }, index) => {
            const stock = stocksBySymbol[symbol];
            return stock ? (
              <ChartCard key={symbol} stock={stock} currentFilters={{ symbol: INDEX_SYMBOLS.map(item => item.symbol).join(',') }} stockIndex={index} />
            ) : (
              <MissingIndexPlaceholder key={symbol} symbol={symbol} name={name} />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IndexesPage;
